import React from 'react'
import { FormattedMessage } from 'react-intl'

import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import Select from 'components/Select'
import TrashBox from 'assets/icons/trash-box'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import { SpecieParameterToggleWrapper } from 'flow/siteProposal/pages/assessment/components/speciesDetails/speciesDetailsModal/style'
import {
  EcosystemRow,
  RowActions,
  DetailColumns,
  DetailSection,
  SectionTitle,
  TimingWrapper,
  TooltipBtnWrapper,
  TooltipBtnText
} from './style'
import CopyIcon from 'assets/icons/copy-icon'
import PasteIcon from 'assets/icons/paste-icon'
import {
  setCopyPayload,
  subscribe,
  getCopyPayload,
  subscribePasteUsed,
  setPasteUsed,
  getPasteUsed,
  setActiveDetail,
  getActiveDetail,
  subscribeActive,
  addActivePaste,
  clearActivePastes
} from './ecosystemCopyStore'
import { requestPressureCopyPaste } from 'api/monitoring'
import { useParams } from 'react-router-dom'
import Tooltip from 'components/Tooltip'
import { TooltipWrapper } from 'flow/SiteMonitoring/pages/Response/style'
import { TooltipHintWrapper } from 'flow/siteProposal/pages/about/style'
import InfoIcon from 'assets/icons/info-icon'
import Modal from '../component/Modal'
import FlowRequestModal from 'components/FlowRequestModal'

const EcosystemAccordionHeader = ({
  item,
  isOpen,
  toggleAccordion,
  getChecked,
  onToggleNode,
  getDepth,
  onOpenDetails,
  onClone,
  onDelete,
  isSpeciesView,
  scopeSelections = {},
  severitySelections = {},
  timingSelections = {},
  updateTaxonGroupScope,
  updateTaxonGroupSeverity,
  updateTaxonGroupTiming,
  isTaxonScopeOptions = [],
  isTaxonSeverityOptions = [],
  isTimingOptions = [],
  monitorThreatId,
  isEditable,
  isReviewMode = false,
  preSelectedAllItems,
  timingAllSelections
}) => {
  const params = useParams()
  const depth = getDepth ? getDepth(item) : 0
  const isChild = depth > 0
  let checked = false
  if (getChecked) {
    // if this item has children, consider it checked only when all species descendants are checked
    if (Array.isArray(item.children) && item.children.length > 0) {
      try {
        checked = item.children.every((c) => getChecked(c))
      } catch (e) {
        checked = Boolean(getChecked(item))
      }
    } else {
      checked = Boolean(getChecked(item))
    }
  } else {
    checked = Boolean(item.isChecked)
  }
  // local UI state for active copy/paste is derived from shared store
  const [copyPayload, setCopyPayloadState] = React.useState(() => getCopyPayload() || null)
  const [isTaxonDeleteModal, setIsTaxonDeleteModal] = React.useState(false)
  const [selectedItem, setSelectedItem] = React.useState(null)
  const instanceId = React.useRef(`ec-header-${Math.random().toString(36).slice(2)}`)
  const threatTimmingId = localStorage.getItem('site_threat_timing_id')

  React.useEffect(() => {
    const unsub = subscribe((p) => setCopyPayloadState(p))
    return () => unsub()
  }, [])

  // subscribe to active detail so header reflects active copy/paste state
  const [activeDetail, setActiveDetailState] = React.useState(() => getActiveDetail())
  React.useEffect(() => {
    const unsubA = subscribeActive((v) => setActiveDetailState(v))
    return () => unsubA()
  }, [])

  // copy visual is driven directly from `copyPayload` and shared active state
  React.useEffect(() => {
    const handler = (e) => {
      const d = e && e.detail ? e.detail : {}
      if (d && d.source === instanceId.current) return
      // When another COPY is selected elsewhere, clear this header's paste visual state
      if (d.button === 'copy' && ((d.scope === 'header' && d.id !== item.id) || d.scope === 'species')) {
        // other copies clear our paste visuals; store will also update subscribers
      }
    }
    document.addEventListener('ecosystem:buttonToggled', handler)

    return () => {
      document.removeEventListener('ecosystem:buttonToggled', handler)
    }
  }, [item.id])

  const handleToggle = (event) => {
    event.stopPropagation()
    toggleAccordion()
  }

  const collectSpeciesNodes = (node) => {
    const results = []
    const walk = (n) => {
      if (!n) return
      if (n.kind === 'species') {
        results.push(n)
        return
      }
      if (Array.isArray(n.children)) {
        n.children.forEach((c) => walk(c))
      }
    }
    walk(node)
    return results
  }

  // disable paste when no copy payload exists; when a copy exists only
  const isEmptyObject = copyPayload && typeof copyPayload === 'object' && Object.keys(copyPayload).length === 0
  // If no copy payload exists, disable paste. If a copy exists, only disable paste for the same taxon.
  const isPasteDisabled = copyPayload ? Boolean(isEmptyObject || copyPayload.copy_from_taxon === item?.name) : true
  const [isPasteDisabledBtn, setIsPasteDisabledBtn] = React.useState(isPasteDisabled || getPasteUsed())

  React.useEffect(() => {
    const unsub = subscribePasteUsed((v) => {
      setIsPasteDisabledBtn(Boolean(isPasteDisabled || v))
    })
    return () => unsub()
  }, [isPasteDisabled])

  // update header paste-disabled when global copy payload changes
  React.useEffect(() => {
    try {
      setIsPasteDisabledBtn(Boolean(isPasteDisabled || getPasteUsed()))
    } catch (e) {}
  }, [isPasteDisabled, copyPayload])

  const handlePaste = (taxonName) => {
    const payload = getCopyPayload()
    if (!payload) return

    const finalPayload = {
      ...payload,
      paste_to_taxon: [taxonName],
      monitor_threats_id: item?.raw?.monitor_threats_id || Number(monitorThreatId) || null,
      site_id: params.id || null,
      site_pressure_id: Number(params.pressureId) || null,
      filter_by: 1
    }
    updateTaxonGroupScope?.(item.id, payload?.scope_id || null)
    updateTaxonGroupSeverity?.(item.id, payload?.severity_id || null)
    updateTaxonGroupTiming?.(item.id, payload?.site_threat_timing_id || null)
    // remove unwanted keys from finalPayload before sending
    ;['scope_id', 'severity_id', 'site_threat_timing_id'].forEach((k) => {
      if (k in finalPayload) delete finalPayload[k]
    })
    //API call
    try {
      setPasteUsed(true)
    } catch (e) {}
    requestPressureCopyPaste(finalPayload)
      .then((response) => {
        //handle response
        console.log('EcosystemAccordionContent handlePaste response: ', response)
      })
      .catch((error) => {
        //handle error
        console.error('EcosystemAccordionContent handlePaste error: ', error)
      })
      .finally(() => {
        try {
          setPasteUsed(false)
        } catch (e) {}
      })
  }

  const commonTooltipProps = {
    style: { zIndex: 9999 },
    placement: 'bottom',
    trigger: ['hover'],
    align: { offset: [0, 16] },
    type: 'big'
  }

  const handleDeleteTaxon = (item) => {
    if (selectedItem) {
      onDelete?.(selectedItem);
    }
    setIsTaxonDeleteModal(false);
    setSelectedItem(null);
  }

  return (
    <>
      <EcosystemRow $isReviewMode={isReviewMode}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isReviewMode ? (
            <span className="species-label">
              <span className="dot" />
              {item.name}
            </span>
          ) : (
          <Checkbox
            checked={checked}
            disabled={!isEditable}
            onChange={(event) => {
              event.stopPropagation()
              onToggleNode?.(item)
            }}
            label={
              <span className="species-label">
                <span className="dot" />
                {item.name}
              </span>
            }
          />
          )}
        </div>
        {!isReviewMode && <div className="type-label"></div>}

        <div className="expand-cell">
          <SpecieParameterToggleWrapper isActive={isOpen} onClick={handleToggle} style={{ cursor: 'pointer' }}>
            {isOpen ? (
              <FormattedMessage id="close" defaultMessage="close" />
            ) : (
              <FormattedMessage id="expand" defaultMessage="expand" />
            )}
            {isOpen ? (
              <DropdownIndicatorIconOpen style={{ marginLeft: 8 }} />
            ) : (
              <DropdownIndicatorIconClosed style={{ marginLeft: 8 }} />
            )}
          </SpecieParameterToggleWrapper>
        </div>

        {!isReviewMode && (
        <RowActions>
          <TooltipBtnWrapper>
            <Button
              height="34px"
              marginTop="0"
              isDisabled={!isEditable}
              type={
                (copyPayload && copyPayload.copy_from_taxon === item?.name && !copyPayload.copy_from_species) ||
                (activeDetail &&
                  activeDetail.copy &&
                  activeDetail.copy.id === item.id &&
                  activeDetail.copy.button === 'copy')
                  ? 'primary'
                  : undefined
              }
              onClick={(event) => {
                event.stopPropagation()
                if (!isEditable) return
                // build payload for group copy
                const scopeValue = scopeSelections?.[item.id] || item?.raw?.scope_id
                const severityValue = severitySelections?.[item.id] || item?.raw?.severity_id
                const timingValue = timingSelections?.[item.id] || item?.raw?.site_threat_timing_id || threatTimmingId
                const resolveId = (val) => {
                  if (!val && val !== 0) return ''
                  if (typeof val === 'object') return val.value ?? val.id ?? ''
                  return val
                }

                const payload = {
                  monitor_threats_id:
                    Number(item?.monitorThreatId) || item?.raw?.monitor_threats_id || item?.monitor_threats_id || null,
                  copy_from_taxon: item?.name || null,
                  site_id: item?.raw?.site_id || item?.site_id || null,
                  scope_id: resolveId(scopeValue),
                  severity_id: resolveId(severityValue),
                  site_threat_timing_id: resolveId(timingValue)
                }

                const existing = getCopyPayload()
                // toggle: if the same group is already copied, clear the payload
                if (existing && existing.copy_from_taxon && existing.copy_from_taxon === payload.copy_from_taxon) {
                  try {
                    setCopyPayload(null)
                  } catch (e) {}
                  try {
                    setActiveDetail(null)
                  } catch (e) {}
                  try {
                    document.dispatchEvent(
                      new CustomEvent('ecosystem:buttonToggled', {
                        detail: { scope: 'header', id: item.id, button: null, source: instanceId.current }
                      })
                    )
                  } catch (e) {}
                  return
                }
                try {
                  document.dispatchEvent(
                    new CustomEvent('ecosystem:buttonToggled', {
                      detail: { scope: 'header', id: item.id, button: 'copy', source: instanceId.current }
                    })
                  )
                } catch (e) {}
                setCopyPayload(payload)
                try {
                  setActiveDetail({ id: item.id, button: 'copy', scope: 'header' })
                } catch (e) {}
                onClone?.(item)
              }}
            >
              <PasteIcon />
            </Button>
            <TooltipBtnText>
              <FormattedMessage id="copy" defaultMessage="copy" />
            </TooltipBtnText>
          </TooltipBtnWrapper>

          <TooltipBtnWrapper>
            <Button
              height="34px"
              marginTop="0"
              isDisabled={!isEditable}
              type={
                activeDetail && Array.isArray(activeDetail.pastes) && activeDetail.pastes.includes(item.id)
                  ? 'primary'
                  : undefined
              }
              disabled={isPasteDisabledBtn}
              onClick={(event) => {
                if (isPasteDisabled) return
                event.stopPropagation()
                try {
                  addActivePaste({ id: item.id, button: 'paste', scope: 'header' })
                } catch (e) {}
                try {
                  document.dispatchEvent(
                    new CustomEvent('ecosystem:pasteButtonToggled', {
                      detail: {
                        scope: 'header',
                        id: item.id,
                        taxon: item?.name,
                        button: 'paste',
                        source: instanceId.current
                      }
                    })
                  )
                } catch (e) {}
                onOpenDetails?.(item)
                handlePaste(item?.name)
                // mark paste as used globally so other paste buttons become disabled
                try {
                  setPasteUsed(true)
                } catch (e) {}
              }}
            >
              <CopyIcon />
            </Button>
            <TooltipBtnText>
              <FormattedMessage id="paste" defaultMessage="paste" />
            </TooltipBtnText>
          </TooltipBtnWrapper>
          <TooltipBtnWrapper>
            <Button
              type="disalbeBorder"
              height="34px"
              marginTop="0"
              isDisabled={!isEditable}
              onClick={(event) => {
                event.stopPropagation()
                if (!isEditable) return
                setSelectedItem(item);
                setIsTaxonDeleteModal(true);
              }}
            >
              <TrashBox />
            </Button>
            <TooltipBtnText>
              <FormattedMessage id="delete" defaultMessage="delete" />
            </TooltipBtnText>
          </TooltipBtnWrapper>
        </RowActions>
        )}
      </EcosystemRow>

      {isOpen && (
        <>
          <DetailColumns style={{ marginTop: 24, marginBottom: 24 }}>
            <DetailSection>
              <SectionTitle>
                {isSpeciesView ? (
                  <FormattedMessage
                    id="onlyForSelectedSpeciesTitle"
                    defaultMessage="onlyForSelectedSpeciesTitle"
                  />
                ) : (
                  <TooltipWrapper>
                    <FormattedMessage
                      id="onlyForSelectedTaxonomicTitle"
                      defaultMessage="onlyForSelectedTaxonomicTitle"
                    />
                    <Tooltip
                      {...commonTooltipProps}
                      overlay={{
                        label: 'onlyForSelectedTaxonomicTitle',
                        infoTooltip: 'onlyForSelectedTaxonomicInfo'
                      }}
                    >
                      <TooltipHintWrapper style={{marginLeft: 0, width: '32px', height: '32px', fontSize: '18px'}}>
                        <InfoIcon width={24} height={24} />
                      </TooltipHintWrapper>
                    </Tooltip>
                  </TooltipWrapper>
                )}
              </SectionTitle>
            </DetailSection>
          </DetailColumns>

          <DetailColumns style={{ display: 'flex', gap: 16, padding: '2px 130px' }}>
            <DetailSection style={{ flex: 1 }}>
              <SectionTitle>
                <FormattedMessage id="ScopeOfThreatTitle" defaultMessage="ScopeOfThreatTitle" />
              </SectionTitle>
              <TimingWrapper style={{ maxWidth: '900px' }}>
                {(() => {
                  const scopeValue = scopeSelections[item.id] || item.raw.scope_id
                  const scopeSelected =
                    scopeValue && typeof scopeValue === 'object'
                      ? scopeValue
                      : isTaxonScopeOptions.find((opt) => (opt && (opt.value ?? opt.id)) === scopeValue) || null
                  return (
                    <Select
                      placeholder="Please select"
                      value={scopeSelected}
                      disabled={!isEditable}
                      onChange={(option) => {
                        updateTaxonGroupScope?.(item.id, option)
                        const species = collectSpeciesNodes(item)
                        try {
                          setCopyPayload(null)
                          try {
                            setActiveDetail(null)
                          } catch (e) {}
                          try {
                            clearActivePastes()
                          } catch (e) {}
                          setIsPasteDisabledBtn(true)
                        } catch (e) {}
                      }}
                      options={isTaxonScopeOptions}
                    />
                  )
                })()}
              </TimingWrapper>
            </DetailSection>

            <DetailSection style={{ flex: 1 }}>
              <SectionTitle>
                <FormattedMessage id="SeverityOfThreatTitle" defaultMessage="SeverityOfThreatTitle" />
              </SectionTitle>
              <TimingWrapper>
                {(() => {
                  const severityValue = severitySelections[item.id] || item.raw.severity_id
                  const severitySelected =
                    severityValue && typeof severityValue === 'object'
                      ? severityValue
                      : isTaxonSeverityOptions.find((opt) => (opt && (opt.value ?? opt.id)) === severityValue) || null
                  return (
                    <Select
                      placeholder="Please select"
                      value={severitySelected}
                      disabled={!isEditable}
                      onChange={(option) => {
                        updateTaxonGroupSeverity?.(item.id, option)
                        const species = collectSpeciesNodes(item)
                        try {
                          setCopyPayload(null)
                          try {
                            setActiveDetail(null)
                          } catch (e) {}
                          try {
                            clearActivePastes()
                          } catch (e) {}
                          setIsPasteDisabledBtn(true)
                        } catch (e) {}
                      }}
                      options={isTaxonSeverityOptions}
                    />
                  )
                })()}
              </TimingWrapper>
            </DetailSection>
          </DetailColumns>

          <DetailSection style={{ padding: '2px 130px' }}>
            <SectionTitle>
              <FormattedMessage id="TimingOfThreatTitle" defaultMessage="TimingOfThreatTitle" />
            </SectionTitle>
            <TimingWrapper>
              {(() => {
                const timingValue = Object.prototype.hasOwnProperty.call(timingSelections, item.id)
                  ? timingSelections[item.id]
                  : item.raw?.site_threat_timing_id || threatTimmingId
                const timingSelected =
                  timingValue && typeof timingValue === 'object'
                    ? timingValue
                    : isTimingOptions.find((opt) => (opt && (opt.value ?? opt.id)) === timingValue) || null
                return (
                  <Select
                    placeholder="selectTiming"
                    value={timingSelected}
                    disabled={!isEditable}
                    onChange={(option) => {
                      updateTaxonGroupTiming?.(item.id, option)
                      const species = collectSpeciesNodes(item)
                      try {
                        setCopyPayload(null)
                        try {
                          setActiveDetail(null)
                        } catch (e) {}
                        try {
                          clearActivePastes()
                        } catch (e) {}
                        setIsPasteDisabledBtn(true)
                      } catch (e) {}
                    }}
                    options={isTimingOptions}
                  />
                )
              })()}
            </TimingWrapper>
          </DetailSection>
        </>
      )}

      <Modal
        width="496px"
        isOpen={isTaxonDeleteModal}
        toggleModal={setIsTaxonDeleteModal}
        modalTitleId={'taxonDeleteModalTitle'}
        bottomBorder={true}
        sideModal={false}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsTaxonDeleteModal(false)
          }}
          confirmHandler={handleDeleteTaxon}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="removeSpeciesModalText" values={{ name: <b>{selectedItem?.name}</b> }} />}
          item={''}
          approve={false}
        />
      </Modal>
    </>
  )
}

export default EcosystemAccordionHeader
